import { Fab, IconButton, List, ListItem, ListItemAvatar, ListItemText, Tooltip, Typography } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import COESkeletonComponent from '../../../components/general/coe-skeleton/coe-skeleton.component';
import { UserAvatar } from '../../../components/user-avatar/user-avatar.component';
import DeleteIcon from '@mui/icons-material/Delete';
import useService from '../../../hooks/service.hook';
import GuardianService from '../../../services/guardian.service';
import useSnackbarHelper from '../../../hooks/snackbar.hook';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import InviteGuardianDialog from '../../../components/dialogs/invite-guardian.dialog';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CloseIcon from '@mui/icons-material/Close';
import { format, parseISO } from 'date-fns';

interface Ward {
  id: string;
  name: string;
  email: string;
}

interface Guardian {
  id: string;
  name: string;
  email: string;
}

interface GuardianInvitation {
  id: string;
  email: string;
  sentAt: string;
}

const GuardianManagementPage: FC = () => {
  // hooks
  const guardianService = useService(GuardianService);
  const { showSnackbar } = useSnackbarHelper();

  // loading states
  const [wardsLoading, setWardsLoading] = useState<boolean>(false);
  const [invitationsLoading, setInvitationsLoading] = useState<boolean>(false);
  const [guardiansLoading, setGuardiansLoading] = useState<boolean>(false);
  const [showGuardianEmailModal, setShowGuardianEmailModal] = useState<boolean>(false);
  const [inviteGuardianLoading, setInviteGuardianLoading] = useState<boolean>(false);

  // values
  const [wards, setWards] = useState<Ward[]>([]);
  const [invitations, setInvitations] = useState<GuardianInvitation[]>([]);
  const [guardians, setGuardians] = useState<Guardian[]>([]);

  useEffect(() => {
    getGuardians();
    getGuardianInvitations();
    getWards();
  }, []);

  const getGuardians = () => {
    setGuardiansLoading(true);
    guardianService
      .getGuardians()
      .then((response) => {
        setGuardians(response.data.data);
        setGuardiansLoading(false);
      })
      .catch(() => {
        setGuardiansLoading(false);
        showSnackbar('There was an error while fetching guardians please try again later', 'error');
      });
  };

  const inviteGuardian = (email: string) => {
    setInviteGuardianLoading(true);
    guardianService
      .inviteGuardian(email)
      .then(() => {
        setInviteGuardianLoading(false);
        setShowGuardianEmailModal(false);
        showSnackbar('Successfully invited guardian', 'success');
        getGuardianInvitations();
      })
      .catch(() => {
        setInviteGuardianLoading(false);
        setShowGuardianEmailModal(false);
        showSnackbar('Error while inviting guardian. Please try again later', 'error');
      });
  };

  const getGuardianInvitations = () => {
    setInvitationsLoading(true);
    guardianService
      .getPendingGuardianInvitations()
      .then((response) => {
        setInvitations(response.data.data);
        setInvitationsLoading(false);
      })
      .catch(() => {
        setInvitationsLoading(false);
        showSnackbar('There was an error while fetching invitations please try again later', 'error');
      });
  };

  const getWards = () => {
    setWardsLoading(true);
    guardianService
      .getWards()
      .then((response) => {
        setWardsLoading(false);
        setWards(response.data.data);
      })
      .catch(() => {
        setWardsLoading(false);
        showSnackbar('There was an error while fetching your supervised please try again', 'error');
      });
  };

  const removeWard = (id: string) => {
    guardianService
      .deleteWard(id)
      .then(() => {
        getWards();
      })
      .catch(() => {
        showSnackbar('There was an error while removing this supervised athlete please try again', 'error');
      });
  };

  const removeGuardian = (id: string) => {
    guardianService
      .removeGuardian(id)
      .then(() => {
        getGuardians();
        showSnackbar('Successfully removed guardian', 'success');
      })
      .catch((error) => {
        if (error?.response?.status === 422) {
          showSnackbar(
            'You cannot remove a guardian while on a team. Please add another guardian to remove this one',
            'error'
          );
        } else {
          showSnackbar('There was an error while removing this guardian please try again', 'error');
        }
      });
  };

  const removeGuardianInvitation = (id: string) => {
    guardianService
      .deactivateGuardianInvitation(id)
      .then(() => {
        getGuardianInvitations();
        showSnackbar('Successfully removed guardian invitation', 'success');
      })
      .catch(() => {
        showSnackbar('There was an error while removing this guardian invitation please try again', 'error');
      });
  };

  const renderGuardians = () => {
    if (guardians.length === 0) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            flexDirection: 'column',
          }}
        >
          <SupervisorAccountIcon fontSize="large" style={{ color: 'grey' }} />
          <Typography variant="h4" color={'grey'}>
            No linked guardians
          </Typography>
        </div>
      );
    }

    return (
      <List style={{ width: '70%' }}>
        {guardians.map((item) => (
          <ListItem
            key={item.id}
            secondaryAction={
              <Tooltip title="Remove guardian. This action cannot be done">
                <IconButton edge="end" aria-label="delete" onClick={() => removeGuardian(item.id)}>
                  <CloseIcon color="error" />
                </IconButton>
              </Tooltip>
            }
          >
            <ListItemAvatar>
              <UserAvatar name={item.name} />
            </ListItemAvatar>
            <ListItemText primary={item.name} secondary={item.email} />
          </ListItem>
        ))}
      </List>
    );
  };

  const renderWards = () => {
    if (wards.length === 0) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 400,
            flexDirection: 'column',
          }}
        >
          <FamilyRestroomIcon fontSize="large" style={{ color: 'grey' }} />
          <Typography variant="h4" color={'grey'}>
            No supervised athletes
          </Typography>
        </div>
      );
    }

    return (
      <List style={{ width: '70%' }}>
        {wards.map((item) => (
          <ListItem
            key={item.id}
            secondaryAction={
              <Tooltip title="Remove supervised athlete. This action cannot be done">
                <IconButton edge="end" aria-label="delete" onClick={() => removeWard(item.id)}>
                  <CloseIcon color="error" />
                </IconButton>
              </Tooltip>
            }
          >
            <ListItemAvatar>
              <UserAvatar name={item.name} />
            </ListItemAvatar>
            <ListItemText primary={item.name} secondary={item.email} />
          </ListItem>
        ))}
      </List>
    );
  };

  const renderInvitations = () => {
    if (invitations.length === 0) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            flexDirection: 'column',
          }}
        >
          <MailOutlineIcon fontSize="large" style={{ color: 'grey' }} />
          <Typography variant="h4" color={'grey'}>
            No guardian invitations sent
          </Typography>
        </div>
      );
    }

    return (
      <List style={{ width: '70%' }}>
        {invitations.map((item) => (
          <ListItem
            key={item.id}
            secondaryAction={
              <Tooltip title="Remove guardian Invitation. This action cannot be done">
                <IconButton edge="end" aria-label="delete" onClick={() => removeGuardianInvitation(item.id)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            }
          >
            <ListItemAvatar>
              <UserAvatar name={item.email} />
            </ListItemAvatar>
            <ListItemText primary={item.email} secondary={`Sent at: ${format(parseISO(item.sentAt), 'MMM do, Y')}`} />
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <div style={{ display: 'flex', width: '100%', flexDirection: 'column', height: 'calc(95vh - 64px)' }}>
      <div style={{ display: 'flex' }}>
        <div style={{ width: '50%', marginRight: 5 }}>
          <Typography variant="h2">Guardians</Typography>
          <div style={{ height: 250, overflow: 'auto' }}>
            {guardiansLoading ? (
              <div style={{ margin: 30 }}>
                <COESkeletonComponent />
                <COESkeletonComponent />
                <COESkeletonComponent />
              </div>
            ) : (
              renderGuardians()
            )}
          </div>
        </div>
        <div style={{ width: '50%' }}>
          <Typography variant="h2">Guardian Invitations</Typography>
          <div style={{ height: 250, overflow: 'auto' }}>
            {invitationsLoading ? (
              <div style={{ margin: 30 }}>
                <COESkeletonComponent />
                <COESkeletonComponent />
                <COESkeletonComponent />
              </div>
            ) : (
              renderInvitations()
            )}
          </div>
        </div>
      </div>
      <div style={{ height: '100%' }}>
        <Typography variant="h2">Supervised Athletes</Typography>
        <div>
          {wardsLoading ? (
            <div style={{ margin: 30 }}>
              <COESkeletonComponent />
              <COESkeletonComponent />
              <COESkeletonComponent />
            </div>
          ) : (
            renderWards()
          )}
        </div>
      </div>
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 25, right: 25 }}
        onClick={() => setShowGuardianEmailModal(true)}
      >
        <GroupAddIcon />
      </Fab>
      <InviteGuardianDialog
        isOpen={showGuardianEmailModal}
        handleClose={() => setShowGuardianEmailModal(false)}
        isLoading={inviteGuardianLoading}
        handleSubmit={(email: string) => inviteGuardian(email)}
      />
    </div>
  );
};

export default GuardianManagementPage;
